import {
  Flex,
  H3,
  H4,
  getSharedBorderStyle,
  Navigation,
  getMediaQueryForBelowBreakpoint,
} from "@taxbit-private/cosmic";
import { useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useGetCase } from "../api/endpoints/cases/caseGetApi";
import {
  useGetCaseTaxCalcStatus,
  isCalcStatusInProgress,
} from "../api/endpoints/cases/caseStatusGetApi";
import { CaseTaxCalStatus } from "../api/endpoints/cases/caseStatusGetApiTypes";
import { Case } from "../api/endpoints/cases/caseTypes";
import { formatDateString } from "../pages/case-list/utils/formatDateString";
import useDartsStore from "../store/useDartsStore";
import getSidebarNavWidth from "../utils/getSideNavBarWidth";
import BannerNotification from "./notifications/BannerNotification";

const NAV_ITEMS = [
  // {
  //   label: "Case Summary",
  //   trackingId: "casesummary",
  //   iconName: "user",
  //   href: "case-summary",
  // },
  {
    label: "Transactions",
    trackingId: "transactions",
    iconName: "book-open",
    href: "transactions",
  },
  {
    label: "Sources",
    trackingId: "source",
    iconName: "link",
    href: "sources/generic-csv",
  },
  {
    label: "Resolution Center",
    trackingId: "resolution-center",
    iconName: "sliders",
    href: "resolution-center",
  },
  {
    label: "Reports",
    trackingId: "reports",
    iconName: "file-text",
    href: "report-list",
  },
  {
    label: "Settings",
    trackingId: "case-settings",
    iconName: "settings",
    href: "case-settings",
  },
] as const;

const AppNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { caseId } = useParams();

  const addBanner = useDartsStore((state) => state.setBanner);
  const clearBanner = useDartsStore((state) => state.clearBanners);
  const isMenuVisible = useDartsStore((state) => state.isMenuVisible);
  const toggleMenu = useDartsStore((state) => state.toggleMenu);

  const { data: caseData, isLoading } = useGetCase(caseId as Case["id"]);
  const { data: caseTaxCalc } = useGetCaseTaxCalcStatus(caseId as string);

  const createNavigationItem = useCallback(
    ({ href, ...item }: (typeof NAV_ITEMS)[number]) => ({
      ...item,
      isActive: location.pathname.includes(href),
      onClick: () => {
        navigate(`${href}`);
        if (isMenuVisible) {
          toggleMenu();
        }
      },
    }),
    [location.pathname, navigate, isMenuVisible, toggleMenu]
  );

  const completeModuleNavigationItems = useMemo(
    () => NAV_ITEMS.map((item) => createNavigationItem(item)),
    [createNavigationItem]
  );

  useEffect(() => {
    if (
      caseTaxCalc &&
      isCalcStatusInProgress(caseTaxCalc.caseStatus as CaseTaxCalStatus)
    ) {
      addBanner(caseId as string, {
        message: "Tax calculation is in progress.",
        shouldShowSpinner: true,
      });
    } else {
      clearBanner();
    }
  }, [addBanner, caseId, caseTaxCalc, clearBanner, isMenuVisible]);

  return (
    <Flex>
      <NavWrapper
        direction="column"
        padding="xxl m m none"
        gap="xl"
        isMenuVisible={isMenuVisible}
      >
        <StyledHeaderContainer direction="column">
          {!isLoading && caseData && (
            <>
              <H3>{caseData.caseName}</H3>
              {caseData?.asedDate && (
                <H4>ASED DATE: {formatDateString(caseData.asedDate)}</H4>
              )}
            </>
          )}
        </StyledHeaderContainer>
        <Flex direction="column" gap="l">
          <Navigation items={completeModuleNavigationItems} />
        </Flex>
      </NavWrapper>
      <NonNavContent isMenuVisible={isMenuVisible}>
        <BannerNotification />
        <Outlet />
      </NonNavContent>
    </Flex>
  );
};

export default AppNavigation;

const NavWrapper = styled(Flex).withConfig<{
  isMenuVisible?: boolean;
}>({
  shouldForwardProp: (propName) => !["isMenuVisible"].includes(propName),
})(
  ({ theme, isMenuVisible }) => `
  background-color: ${theme.color.white};
  border-right: ${getSharedBorderStyle(theme)};
  min-width: ${getSidebarNavWidth(theme)};
  position: fixed;
  top: 0;
  height: 100vh;
  z-index: 1;
  overflow: auto;
  ${getMediaQueryForBelowBreakpoint(theme, "tablet")} {
     ${isMenuVisible ? "display: flex;" : "display: none;"}
  }
`
);

const NonNavContent = styled.div.withConfig<{
  isMenuVisible?: boolean;
}>({
  shouldForwardProp: (propName) => !["isMenuVisible"].includes(propName),
})(
  ({ theme, isMenuVisible }) => `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background: ${theme.color.gray0};
  width: 100%;
  margin-top: ${theme.sizing.xl};
  margin-left: ${getSidebarNavWidth(theme)};

 ${getMediaQueryForBelowBreakpoint(theme, "tablet")} {
     margin-left: ${
       isMenuVisible
         ? `calc(${getSidebarNavWidth(theme)} + ${theme.spacing.m})`
         : 0
     };


  }
`
);

const StyledHeaderContainer = styled(Flex)(
  ({ theme }) => `
    margin-top: ${theme.sizing.m};
    padding-left: ${theme.spacing.l};
    min-height: ${theme.sizing.l};
    width: ${theme.measure.s};
`
);
